import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ArrowUp, ArrowDown, Note, Car, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
    bediening_voertuig: { icon: '🚗', name: 'Bediening Voertuig' },
    verkeersdeelneming: { icon: '🚦', name: 'Verkeersdeelneming' },
    bijzondere_verrichtingen: { icon: '🅿️', name: 'Bijzondere Verrichtingen' },
};

const SCORE_LABEL = ['—', 'Onvoldoende', 'Matig', 'Voldoende', 'Goed', 'Uitstekend'];

function formatNlDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });
    } catch { return iso; }
}

/**
 * Shown after the daily CBR question for students who have an unread
 * lesson summary from their previous lesson.
 */
export default function LessonSummaryModal({ studentName, onClose }) {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [shown, setShown] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/lesson-summaries/mine/latest`, { withCredentials: true });
                if (!cancelled) setSummary(data);
            } catch {
                if (!cancelled) setSummary(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const close = async () => {
        setShown(false);
        if (summary?.id) {
            try { await axios.post(`${API}/lesson-summaries/${summary.id}/read`, {}, { withCredentials: true }); } catch {}
        }
        setTimeout(() => onClose && onClose(), 300);
    };

    if (loading || !summary) return null;

    const categories = summary.categories || {};
    const averages = summary.category_averages || {};
    const hasContent = Object.keys(categories).length > 0;

    return (
        <AnimatePresence>
            {shown && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    data-testid="lesson-summary-modal"
                >
                    <motion.div
                        initial={{ scale: 0.92, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.92, y: 20 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                        className="bg-[#2D3748] border border-[#4A5568] rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
                    >
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 border-b border-[#4A5568] flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <Car size={22} weight="fill" className="text-[#D4A537]" />
                                    <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Samenvatting vorige les
                                    </h2>
                                </div>
                                <p className="text-sm text-gray-400">{formatNlDate(summary.date)}</p>
                            </div>
                            <button
                                onClick={close}
                                className="text-gray-400 hover:text-white transition-colors p-1 -m-1"
                                data-testid="close-lesson-summary"
                                aria-label="Sluiten"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 space-y-5">
                            {!hasContent ? (
                                <div className="text-center py-6">
                                    <div className="text-5xl mb-3">🚗</div>
                                    <h3 className="text-white font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Goed bezig, {studentName?.split(' ')[0] || 'leerling'}!
                                    </h3>
                                    <p className="text-gray-300 text-sm leading-relaxed">
                                        Vorige les zijn er geen wijzigingen vastgelegd. Dat betekent meestal dat alles soepel verliep — pak je volgende les met goede moed op! 💪
                                    </p>
                                </div>
                            ) : (
                                Object.entries(categories).map(([catKey, catData]) => {
                                    const meta = CATEGORY_META[catKey] || { icon: '📋', name: catKey };
                                    const avg = averages[catKey];
                                    const scoreChanges = catData.score_changes || [];
                                    const noteChanges = catData.note_changes || [];
                                    return (
                                        <div key={catKey} className="bg-[#1A202C] rounded-lg p-4 border border-[#4A5568]">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xl" aria-hidden>{meta.icon}</span>
                                                    <h4 className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                        {meta.name}
                                                    </h4>
                                                </div>
                                                {avg !== undefined && (
                                                    <div className="px-2.5 py-0.5 rounded-full bg-[#D4A537]/15 text-[#D4A537] text-xs font-semibold tabular-nums">
                                                        ⭐ {avg.toFixed(1)}
                                                    </div>
                                                )}
                                            </div>

                                            {scoreChanges.length > 0 && (
                                                <ul className="space-y-1.5 mb-3">
                                                    {scoreChanges.map(ch => {
                                                        const up = ch.after > ch.before;
                                                        return (
                                                            <li key={ch.skill_id} className="flex items-center gap-2 text-sm text-gray-200">
                                                                {up ? (
                                                                    <ArrowUp size={14} weight="bold" className="text-emerald-400 flex-shrink-0" />
                                                                ) : (
                                                                    <ArrowDown size={14} weight="bold" className="text-orange-400 flex-shrink-0" />
                                                                )}
                                                                <span className="flex-1">{ch.name}</span>
                                                                <span className="text-xs text-gray-500">{ch.before} → </span>
                                                                <span className={`text-xs font-bold ${up ? 'text-emerald-400' : 'text-orange-400'}`}>{ch.after}</span>
                                                                <span className="text-xs text-gray-500 hidden sm:inline">({SCORE_LABEL[ch.after]})</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}

                                            {noteChanges.length > 0 && (
                                                <div className="space-y-2">
                                                    {noteChanges.map(nc => (
                                                        <div key={nc.skill_id} className="flex items-start gap-2 text-sm bg-[#2D3748] rounded-md p-2.5">
                                                            <Note size={14} className="text-[#D4A537] mt-0.5 flex-shrink-0" />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-xs text-gray-500 mb-0.5">{nc.name}</div>
                                                                <div className="text-gray-200 italic">"{nc.after}"</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 pb-6 pt-2">
                            <button
                                onClick={close}
                                className="w-full bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                                data-testid="acknowledge-lesson-summary"
                            >
                                <CheckCircle size={18} weight="fill" />
                                Bekeken, op naar de volgende les!
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
